import { LatLngBounds } from "leaflet"
import type { Bounds, LatLng, Map as LeafletMap, Point } from "leaflet"
import { useEffect, useState } from "react"
import type { ReactElement } from "react"
import { useMap } from "react-leaflet"
import type { ICircle, IMapDrawing, IPolyline, IRectangle } from "../../../redux"
import {
  convertRadiusInMetersToPixels,
  getPixelHeightFromBounds,
  getPixelWidthFromBounds,
  getPolylinePixelBounds
} from "../../../utils"

export interface ILeafletDragHandleStylerProps {
  drawing: IMapDrawing
  isSelected: boolean
}

export function LeafletDragHandleStyler(props: ILeafletDragHandleStylerProps): ReactElement {
  const { drawing, isSelected } = props
  const [styleText, setStyleText] = useState<string>("")
  const [isMounted, setIsMounted] = useState<boolean>(false)
  const map: LeafletMap = useMap()

  useEffect(() => {
    setIsMounted(true)

    return () => {
      setIsMounted(false)
    }
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    if (!isSelected) {
      clearStyling()
      map.off("zoom", setupStyling)
    }
    if (isSelected) {
      setupStyling()
      map.on("zoom", setupStyling)
    }
  }, [drawing, isSelected])

  function setupStyling(): void {
    if (!drawing) return
    const zoom: number = map.getZoom()

    switch (drawing.shape.type) {
      case "rectangle":
        setupRectangleStyles(zoom)
        break
      case "circle":
        setupCircleStyles(zoom)
        break
      case "arrow":
        setupPolylineStyles()
        break
      case "polyline":
        setupPolylineStyles()
    }
  }

  function setupRectangleStyles(zoom: number): void {
    const shape: IRectangle = drawing.shape as IRectangle
    const bounds: LatLngBounds = new LatLngBounds(shape.corner1, shape.corner2)
    let width: number = getPixelWidthFromBounds(bounds, map, zoom) / 10
    let height: number = getPixelHeightFromBounds(bounds, map, zoom) / 10
    const lineWidth: number = shape.lineWidth / 10

    width -= lineWidth
    height -= lineWidth

    const marginLeft: number = (width + lineWidth) / 2
    const marginTop: number = (height + lineWidth) / 2

    const newStyle: string = `
            #app .leaflet-edit-move {
                width: ${width}rem !important;
                height: ${height}rem !important;
                margin-left: -${marginLeft}rem !important;
                margin-top: -${marginTop}rem !important;
                background-color: transparent;
                border: none;
                z-index: 0 !important;
                transition: all 0.25s cubic-bezier(0, 0, 0.25, 1);
            }
        `

    setUnmountSafeStyle(newStyle)
  }

  function setupCircleStyles(zoom: number): void {
    const shape: ICircle = drawing.shape as ICircle
    const radius: number = convertRadiusInMetersToPixels(shape.center, shape.radius, map, zoom)

    let width: number = (radius * 2) / 10
    let height: number = (radius * 2) / 10
    const lineWidth: number = shape.lineWidth / 10

    width -= lineWidth
    height -= lineWidth

    const marginLeft: number = (width + lineWidth) / 2
    const marginTop: number = (height + lineWidth) / 2

    const newStyle: string = `
		    #app .leaflet-edit-move {
		        width: ${width}rem !important;
		        height: ${height}rem !important;
		        margin-left: -${marginLeft}rem !important;
		        margin-top: -${marginTop}rem !important;
                border-radius: 50%;
                z-index: 0 !important;
                border: none;
                background-color: transparent;
                transition: all 0.25s cubic-bezier(0, 0, 0.25, 1);
		    }
		`

    setUnmountSafeStyle(newStyle)
  }

  function setupPolylineStyles(): void {
    const shape: IPolyline = drawing.shape as IPolyline
    const points: Point[] = shape.positions.map((latlng: LatLng) => {
      return map.latLngToLayerPoint(latlng)
    })
    const bounds: Bounds = getPolylinePixelBounds(points)
    const size: Point = bounds.getSize()
    const lineWidth: number = shape.lineWidth / 10

    const marginLeft: number = (size.x / 10 + lineWidth) / 2
    const marginTop: number = (size.y / 10 + lineWidth) / 2

    const newStyle: string = `
            #app .leaflet-edit-move {
                width: ${size.x / 10}rem !important;
                height: ${size.y / 10}rem !important;
                margin-left: -${marginLeft}rem !important;
                margin-top: -${marginTop}rem !important;
                background-color: transparent;
                z-index: 0 !important;
                border: 0.2rem solid #009fe3;
            }
        `

    setUnmountSafeStyle(newStyle)
  }

  function setUnmountSafeStyle(value: string): void {
    if (!isMounted) return
    setStyleText(value)
  }
  function clearStyling(): void {
    setUnmountSafeStyle("")
  }

  // biome-ignore lint/security/noDangerouslySetInnerHtml: TODO in a later PR
  // biome-ignore lint/style/useSelfClosingElements: TODO in a later PR
  return <style dangerouslySetInnerHTML={{ __html: styleText }}></style>
}
